/* ==========================================================================
   pack-selector.js — shared pack selector behaviour

   Quantity based. Selecting a pack writes its quantity into the product form;
   the existing cart button then submits base_variant + quantity and Shopify
   applies the real automatic discount. Nothing here computes a cart price.

   Deliberately does NOT depend on:
     - .pricevariant hidden markup
     - Buy 3 / Buy 6 variant ids
     - variant option string parsing
     - positional variant indexes
   so it keeps working once those variants are deleted.

   PRICING THE BUTTON
   The discounts are configured with a MINIMUM quantity, so quantity 4 still
   earns the Buy 3 rate. The button therefore has to price any quantity, not
   just the three on the cards: find the highest tier whose quantity is less
   than or equal to the current one, and use its percentage.

   Rounding follows Shopify exactly - the discount is FLOORED per unit, then
   multiplied. Rounding up instead loses a cent on every unit: at quantity 7
   and 25% that is $130.97 against the cart's $131.04.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__packSelectorReady) { return; }
  window.__packSelectorReady = true;

  var ROOT = '[data-pack-selector]';
  var POLL_MS = 200;

  function init(root) {
    if (!root || root.__packInit) { return; }
    root.__packInit = true;

    // GUARD - never fall back to `document`.
    //
    // This previously ended in `|| document`, which meant that on a page with
    // no product form the script operated on the WHOLE page: it could overwrite
    // the first .gf_add-to-cart .price anywhere on that page, write into any
    // quantity input, and run its interval for nothing. On a listing page full
    // of product tiles that is capable of showing a wrong price.
    //
    // If there is no product form, this selector has no business on the page.
    var form = root.closest('form.AddToCartForm') || root.closest('form');
    if (!form) { return; }
    var options = [].slice.call(root.querySelectorAll('[data-pack-qty]'));
    var currentQty = root.getAttribute('data-default-qty') || '1';
    var baseVariant = root.getAttribute('data-base-variant') || '';

    var data = { moneyFormat: '${{amount}}', tiers: [], variants: {} };
    try {
      var parsed = JSON.parse(root.getAttribute('data-pack'));
      if (parsed) { data = parsed; }
    } catch (err) { /* keep defaults; cards still render from Liquid */ }

    // Highest tier first, so the first match when scanning is the best one.
    var tiers = (data.tiers || []).slice().sort(function (a, b) { return b.q - a.q; });

    /* --- helpers -------------------------------------------------------- */

    function variantId() {
      var field = form.querySelector && form.querySelector('input[name="id"], select[name="id"]');
      if (field && field.value) { return String(field.value); }
      if (baseVariant) { return baseVariant; }
      for (var k in data.variants) { if (data.variants.hasOwnProperty(k)) { return k; } }
      return null;
    }

    function variantData() { return data.variants[variantId()] || null; }

    function quantityField() {
      return form.querySelector && form.querySelector('input[name="quantity"]');
    }

    function normalise(value) {
      var n = parseInt(value, 10);
      return isNaN(n) ? '' : String(n);
    }

    // Discounts use "minimum quantity", so quantity 4 earns the Buy 3 rate.
    function percentFor(qty) {
      var n = parseInt(qty, 10);
      if (isNaN(n)) { return 0; }
      for (var i = 0; i < tiers.length; i++) {
        if (n >= tiers[i].q) { return tiers[i].p || 0; }
      }
      return 0;
    }

    // Shopify's own formatter when present, so Markets and multi-currency
    // stay correct; a plain fallback otherwise.
    function money(cents) {
      var S = window.Shopify;
      if (S && typeof S.formatMoney === 'function') {
        try { return S.formatMoney(cents, data.moneyFormat); } catch (e) { /* fall through */ }
      }
      var n = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return String(data.moneyFormat).replace(/\{\{\s*amount[^}]*\}\}/, n);
    }

    // Pre-formatted by Liquid for the three cards; computed for anything else.
    function totalFor(qty) {
      var v = variantData();
      if (!v) { return null; }
      if (v.cards && v.cards[String(qty)]) { return v.cards[String(qty)].total; }
      var n = parseInt(qty, 10);
      if (isNaN(n) || n < 1) { return null; }
      var pct = percentFor(n);
      var unitDiscount = Math.floor(v.cents * pct / 100);   // floored per unit, as Shopify does
      return money((v.cents - unitDiscount) * n);
    }

    /* --- writes --------------------------------------------------------- */

    function setQuantity(qty) {
      if (!form.querySelectorAll) { return; }
      [].forEach.call(
        form.querySelectorAll('input[name="quantity"], input[name="quantity_na"]'),
        function (input) {
          if (input.value === String(qty)) { return; }
          input.value = qty;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      );
    }

    // Re-queried each time: some cart scripts rebuild the button after an add.
    function paintButton(qty) {
      var total = totalFor(qty);
      if (total === null || !form.querySelector) { return; }
      var target = form.querySelector('div[data-key=p-cart-button] .gf_add-to-cart .price')
                || form.querySelector('.gf_add-to-cart .price');
      if (target) { target.innerHTML = total; }
    }

    function repaintCards() {
      var v = variantData();
      if (!v || !v.cards) { return; }
      options.forEach(function (option) {
        var card = v.cards[option.getAttribute('data-pack-qty')];
        if (!card) { return; }
        var price = option.querySelector('[data-pack-unit-price]');
        var save = option.querySelector('[data-pack-save]');
        if (price) { price.textContent = card.unit; }
        if (save) { save.hidden = !(card.pct > 0); }
      });
      paintButton(currentQty);
    }

    function clearSelection() {
      options.forEach(function (option) {
        option.classList.remove('is-selected');
        option.setAttribute('aria-checked', 'false');
      });
    }

    function select(qty, pushQuantity) {
      currentQty = String(qty);
      options.forEach(function (option) {
        var active = option.getAttribute('data-pack-qty') === currentQty;
        option.classList.toggle('is-selected', active);
        option.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      if (pushQuantity !== false) { setQuantity(currentQty); }
      paintButton(currentQty);
    }

    /* --- events --------------------------------------------------------- */

    root.addEventListener('click', function (event) {
      var option = event.target.closest && event.target.closest('[data-pack-qty]');
      if (option) { select(option.getAttribute('data-pack-qty')); }
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') { return; }
      var option = event.target.closest && event.target.closest('[data-pack-qty]');
      if (!option) { return; }
      event.preventDefault();
      select(option.getAttribute('data-pack-qty'));
    });

    if (form.addEventListener) {
      form.addEventListener('change', function (event) {
        if (event.target && event.target.name === 'id') { repaintCards(); }
      });
    }

    select(currentQty);

    /* --- stepper watcher -------------------------------------------------
       The GemPages quantity stepper sets the input with jQuery .val(), which
       emits no event, and may do so inside its own async callback. Polling is
       the only reliable signal.

       An off-tier quantity clears the card highlight but STILL prices the
       button, because Shopify still discounts it.
       -------------------------------------------------------------------- */
    var lastSeen = normalise(quantityField() && quantityField().value);
    window.setInterval(function () {
      var field = quantityField();
      if (!field) { return; }
      var now = normalise(field.value);
      if (now === lastSeen) { return; }
      lastSeen = now;
      var match = options.filter(function (option) {
        return option.getAttribute('data-pack-qty') === now;
      })[0];
      if (match) {
        select(now, false);
      } else {
        clearSelection();
        currentQty = now;
        paintButton(now);
      }
    }, POLL_MS);
  }

  function boot() {
    [].forEach.call(document.querySelectorAll(ROOT), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
